import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Spinner, Alert } from 'reactstrap';
import { authApi, tokenExpiryFields } from '../../services/api';

const GoogleCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleGoogleCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError(`Google authentication failed: ${errorParam}`);
        setLoading(false);
        return;
      }

      if (!code) {
        setError('No authorization code received from Google');
        setLoading(false);
        return;
      }

      try {
        // Exchange authorization code for tokens
        const data = await authApi.googleCallback(code);

        // Save authentication data to localStorage
        const authData = {
          token: data.access_token,
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          user: data.user,
          ...tokenExpiryFields(data.expires_in),
        };

        localStorage.setItem('authUser', JSON.stringify(authData));

        // Redirect based on user role and setup status
        if (data.user.role === 'admin' || data.user.is_admin) {
          window.location.href = '/admin/dashboard';
        } else if (!data.user.setup_completed) {
          // New users go to setup wizard
          window.location.href = '/setup';
        } else {
          window.location.href = '/dashboard';
        }
      } catch (err: any) {
        console.error('Google OAuth error:', err);
        setError(err.message || 'Failed to authenticate with Google');
        setLoading(false);
      }
    };

    handleGoogleCallback();
  }, [searchParams, navigate]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
      }}
    >
      <Container className="text-center">
        {loading ? (
          <div>
            <Spinner
              style={{ width: '3rem', height: '3rem' }}
              color="light"
            />
            <h4 className="text-white mt-4">Signing you in with Google...</h4>
            <p className="text-white-50">Please wait while we complete your authentication</p>
          </div>
        ) : error ? (
          <div>
            <Alert color="danger" className="d-inline-block">
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </Alert>
            <div className="mt-4">
              <a href="/login" className="btn btn-primary">
                Back to Login
              </a>
              <a href="/" className="btn btn-light ms-2">
                Go to Home
              </a>
            </div>
          </div>
        ) : null}
      </Container>
    </div>
  );
};

export default GoogleCallback;
